/* eslint-disable global-require */
import React, { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

// types
import { CategoriesResponse, CmsHeroData } from '../../../../types/types'

// utils
import { PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'
import { MyLocationContext } from '../../../../utils/myLocationProvider'
import { HERO_CATEGORIES_COUNT_DESKTOP, HERO_CATEGORIES_COUNT_MOBILE } from '../../utils'
import { getHomePageHeroCategoryClickEvent } from '../../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../../utils/dataLayer'

// styles
import * as SC from './HeroSectionStyles'

// hooks
import useMessages from '../../../../hooks/useMessages'

type Props = {
	categoriesData: CategoriesResponse
	cmsData: CmsHeroData
}

const LoadingCategory = () => {
	const { assetsPath } = useContext(AppContext)

	const loadingCategoryPlaceholder = `${assetsPath}/${require('../../../../assets/images/hero-categories-loading.jpg')}`
	const loadingCategoryPlaceholderWebP = `${assetsPath}/${require('../../../../assets/images/hero-categories-loading.webp')}`

	return (
		<SC.CategoryImagePicture>
			<source srcSet={loadingCategoryPlaceholderWebP} type='image/webp' />
			<SC.CategoryImage src={loadingCategoryPlaceholder} alt={''} width={20} height={20} />
		</SC.CategoryImagePicture>
	)
}

// component
const HeroSection = (props: Props) => {
	const { locale } = useIntl()

	const { categoriesData, cmsData } = props
	const { background, title, subtitle } = cmsData

	const { assetsPath } = useContext(AppContext)
	const { myLocation } = useContext(MyLocationContext)

	const { messages } = useMessages()

	const categoriesToShow = categoriesData.categories.slice(0, HERO_CATEGORIES_COUNT_DESKTOP)
	const showMoreCategoriesMobile = categoriesData.categories.length > HERO_CATEGORIES_COUNT_MOBILE
	const showMoreCategoriesDesktop = categoriesData.categories.length > HERO_CATEGORIES_COUNT_DESKTOP

	const [loading, setLoading] = useState(true)

	useEffect(() => {
		const imagesToCheck = categoriesData.categories.slice(0, HERO_CATEGORIES_COUNT_MOBILE).map((category) => category.image.resizedImages.thumbnail)

		const checkImagesLoaded = async () => {
			const promises = imagesToCheck.map((src) => {
				return new Promise((resolve) => {
					const img = new Image()
					img.src = src
					img.onload = () => resolve(true)
					img.onerror = () => resolve(false)
				})
			})

			const results = await Promise.all(promises)

			if (results.every(Boolean)) {
				setLoading(false)
			}
		}

		checkImagesLoaded()
	}, [categoriesData.categories])

	return (
		<SC.HeroSection
			$mobileBg={background.mobile}
			$desktopBg={background.desktop}
			$mobileBgWebp={background.mobileWebP}
			$desktopBgWebp={background.desktopWebP}
		>
			<SC.HeroContainer>
				<SC.HeroMainHeading>{title}</SC.HeroMainHeading>
				<SC.HeroDescription>{subtitle}</SC.HeroDescription>
				<SC.HeroCategoriesContainer>
					{categoriesToShow.map((category) => {
						return (
							<SC.CategoryCard
								key={category.id}
								href={PAGE_LINKS['/salons/all/:categorySlug'](locale, category.seoSlug || category.id, { ...myLocation })}
								onClick={() => {
									pushToDataLayer(
										getHomePageHeroCategoryClickEvent(
											category.name || category.seoSlug || category.id,
											PAGE_LINKS['/salons/all/:categorySlug'](locale, category.seoSlug || category.id)
										)
									)
								}}
							>
								{loading ? (
									<LoadingCategory />
								) : (
									<SC.CategoryImage loading='lazy' src={category.image.resizedImages.thumbnail} alt='' width={20} height={20} />
								)}
								<SC.CategoryTitle>{category.name}</SC.CategoryTitle>
							</SC.CategoryCard>
						)
					})}
					<SC.CategoryCard
						href={PAGE_LINKS['/salons/all'](locale, { ...myLocation })}
						$hideOnMobile={!showMoreCategoriesMobile}
						$hideOnDesktop={!showMoreCategoriesDesktop}
						aria-label={messages['And more salons']}
						onClick={() => {
							pushToDataLayer(getHomePageHeroCategoryClickEvent(messages['&  more...'], PAGE_LINKS['/salons/all'](locale)))
						}}
					>
						{loading ? (
							<LoadingCategory />
						) : (
							<SC.CategoryImagePicture>
								<source srcSet={`${assetsPath}/${require('../../../../assets/images/hero-more-categories.webp')}`} type='image/webp' />
								<SC.CategoryImage
									src={`${assetsPath}/${require('../../../../assets/images/hero-more-categories.png')}`}
									alt={''}
									width={20}
									height={20}
								/>
							</SC.CategoryImagePicture>
						)}
						<SC.CategoryTitle>{messages['&  more...']}</SC.CategoryTitle>
					</SC.CategoryCard>
				</SC.HeroCategoriesContainer>
			</SC.HeroContainer>
		</SC.HeroSection>
	)
}

export default HeroSection
